/*
This represents a translation table for the stock exchange to currency.
these stock exchanges are used in the worldwide API to get the stock data.
*/

import type { FetchedStock, FetchedTradingSecurity, Fiat } from "~/types/global.types";

export const currencyExchange = {
  ASX: "AUD",
  NSE: "INR",
  STO: "SEK",
  HEL: "EUR",
  CPH: "DKK",
  TAL: "TWD",
  NASDAQ: "USD",
  JKT: "IDR",
  SES: "SGD",
  HKSE: "HKD",
  IST: "TRY",
  SAU: "SAR",
  MIL: "EUR",
  LSE: "GBP",
  XETRA: "EUR",
  JNB: "ZAR",
  AMS: "EUR",
  EURONEXT: "EUR",
  WSE: "PLN",
  CAI: "EGP",
  OSL: "NOK",
  SIX: "CHF",
  BSE: "INR",
  KLS: "MYR",
  DOH: "QAR",
  BRU: "EUR",
  LIS: "EUR",
  PRA: "EUR",
  DFM: "AED",
  STU: "STU",
  VIE: "EUR",
  KUW: "KWD",
  CBT: "DKK",
  Paris: "EUR",
  BER: "EUR",
  MUN: "EUR",
  HAM: "EUR",
  ISE: "EUR",
  IOB: "INR",
  BUD: "HUF",
  SHZ: "CNY",
  FGI: "JPY",
  SHH: "CNY",
  RIS: "RUB",
  TWO: "TWD",
  FKA: "JPY",
  NYSE: "USD",
  KOE: "KRW",
  KSC: "KRW",
  DUS: "EUR",
  JPX: "JPY",
  TAI: "TWD",
  SET: "THB",
  NZE: "NZD",
  SAO: "BRL",
  TSX: "CAD",
  NEO: "CAD",
  SGO: "CLP",
  AMEX: "USD",
  ASE: "EUR",
  CNQ: "CAD",
  MEX: "MXN",
  BUE: "ARS",
  ATH: "EUR",
  AQS: "AZN",
  TLO: "EUR",
  MCX: "INR",
  MCE: "RUB",
  KOSDAQ: "KRW",
  ETF: "USD",
  TASEUSD: "USD",
  TASE: "ILS",
} as const;

/**
 * TASE funds whose *units* are dollar-quoted (price is in USD), not merely
 * ILS-quoted funds that invest in dollar assets ("כספית דולרית").
 * Matches scrapeFunds.ts: "$" / "נקובה דולר", plus feminine "נקובת דולר"
 * and "נקובה בדולר". Currency-hedged names ("מנוטרל") stay ILS.
 */
export function isTaseUsdQuotedName(name?: string | null): boolean {
  const n = String(name ?? "");
  if (!n || n.includes("מנוטרל")) return false;
  if (/[$＄]/.test(n)) return true;
  return /נקוב[הת]?\s*ב?דולר/.test(n);
}

function taseDisplayName(
  stock: FetchedStock | FetchedTradingSecurity
): string {
  return [stock.symbol, "name" in stock ? stock.name : ""]
    .filter(Boolean)
    .join(" ");
}

export const getCurrency = (
  stock: FetchedStock | FetchedTradingSecurity,
  fiat: Fiat[]
): Fiat | undefined => {
  if (stock.currency) return fiat.find((currency) => currency.value === stock.currency)
  const exchange = stock.exchange_market_short as keyof typeof currencyExchange;
  const isTaseExchange = !exchange || exchange === "TASE" || exchange === "TASEUSD";
  if (isTaseExchange && isTaseUsdQuotedName(taseDisplayName(stock))) {
    return fiat.find((currency) => currency.value === "USD");
  }
  if (!exchange) return undefined;
  const exCurrency = currencyExchange[exchange];
  const currencyObj = fiat.find((currency) => currency.value === exCurrency);
  return currencyObj;
};
